import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/checkbox';
import { Label } from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { Tooltip } from '@/shared/ui/tooltip';
import { useConfirm } from '@/shared/components/confirm';
import { useAiGlobalSettings, useSaveAiGlobalSettings } from '../api/use-ai-admin';
import { formatUsd, parseUsdText, usdToText } from '../model/format';
import type { AiGlobalSettings } from '../model/types';
import { ModelPicker } from './model-picker';
import { UsdInput } from './usd-input';

/** Padrão para todo mundo que não tem override em passbook, loja ou usuário. */
export function GlobalSettingsCard() {
  const { data: saved, isLoading } = useAiGlobalSettings();
  const save = useSaveAiGlobalSettings();
  const confirm = useConfirm();

  const [draft, setDraft] = useState<AiGlobalSettings | null>(null);
  const [limitText, setLimitText] = useState('');
  const [clearUserLimits, setClearUserLimits] = useState(false);

  useEffect(() => {
    if (!saved) return;
    setDraft(saved);
    setLimitText(usdToText(saved.monthlyLimitUsd));
  }, [saved]);

  if (isLoading || !draft || !saved) {
    return (
      <Card className="p-4 sm:p-5">
        <Skeleton className="h-16 w-full" />
      </Card>
    );
  }

  const limit = parseUsdText(limitText);
  const limitInvalid = limit === null || limit === undefined;
  const next: AiGlobalSettings = {
    ...draft,
    monthlyLimitUsd: limitInvalid ? saved.monthlyLimitUsd : limit,
  };
  const dirty =
    clearUserLimits ||
    (Object.keys(next) as Array<keyof AiGlobalSettings>).some((key) => next[key] !== saved[key]);

  const submit = async () => {
    if (clearUserLimits) {
      const ok = await confirm({
        title: 'Aplicar o limite a todos os usuários?',
        description: `Quem tem limite personalizado passa a usar ${formatUsd(next.monthlyLimitUsd)}. Limites de passbook e loja continuam valendo.`,
        confirmLabel: 'Aplicar a todos',
      });
      if (!ok) return;
    }
    save.mutate(
      { ...next, clearUserLimits },
      {
        onSuccess: (result) => {
          setClearUserLimits(false);
          toast.success(
            result.clearedUserLimits > 0
              ? `Padrão salvo. ${result.clearedUserLimits} usuário(s) voltaram ao limite padrão.`
              : 'Padrão global salvo.',
          );
        },
        onError: () => toast.error('Não foi possível salvar o padrão global. Tente novamente.'),
      },
    );
  };

  return (
    <Card className="p-4 sm:p-5">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Padrão global</h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_auto_auto] lg:items-end">
        <div className="min-w-0 space-y-1.5">
          <Label>Modelo de IA</Label>
          <ModelPicker
            value={draft.model}
            onChange={(model) => model && setDraft({ ...draft, model })}
          />
        </div>

        <div className="min-w-0 space-y-1.5">
          <span className="flex items-center gap-1.5">
            <Label htmlFor="global-limit" className="whitespace-nowrap">
              Limite por usuário/mês
            </Label>
            <Tooltip content="O usuário só vê o percentual usado, nunca o valor. 0 bloqueia o uso." />
          </span>
          <UsdInput
            id="global-limit"
            value={limitText}
            onValueChange={setLimitText}
            aria-invalid={limitInvalid}
          />
        </div>

        <div className="space-y-1.5">
          <span className="flex items-center gap-1.5">
            <Label htmlFor="global-renew" className="whitespace-nowrap">
              Renovar todo mês
            </Label>
            <Tooltip content="Com renovação, o consumo zera no dia 1º. Sem renovação, o limite vira saldo." />
          </span>
          <div className="flex h-10 items-center">
            <Switch
              id="global-renew"
              checked={draft.renewMonthly}
              onCheckedChange={(renewMonthly) => setDraft({ ...draft, renewMonthly })}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 sm:col-span-2 lg:col-span-1 lg:h-10 lg:justify-end">
          <span className="flex items-center gap-2">
            <Checkbox
              id="global-clear"
              checked={clearUserLimits}
              onCheckedChange={(checked) => setClearUserLimits(checked === true)}
            />
            <Label htmlFor="global-clear" className="cursor-pointer whitespace-nowrap font-normal">
              Aplicar a todos
            </Label>
            <Tooltip content="Remove os limites personalizados por usuário para todos seguirem este padrão." />
          </span>
          <Button
            disabled={!dirty || limitInvalid}
            loading={save.isPending}
            onClick={() => void submit()}
          >
            Salvar
          </Button>
        </div>
      </div>
      {limitInvalid ? (
        <p className="mt-2 text-xs text-destructive">Informe um limite maior ou igual a zero.</p>
      ) : null}
    </Card>
  );
}
